import { type Ref, computed } from 'vue';

import { DataSourceSettingsTabType } from '@/components/data-sources/SettingsModal/constants';

import { getFeedStatusUi, statuses } from '@/composables/useFeedDetails';
import useSalesEdgeIntegrationStatus from '@/composables/useSalesEdgeIntegrationStatus';
import { HS3_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useFeatureFlagStore, useFeedsStore } from '@/stores';
import { IntegrationProfileStatus } from '@/types/integration_profiles';

const HEALTHY_FEED_STATUSES: string[] = [statuses.ACTIVE, statuses.SETTING_UP];

export function useIsHubspotCopilotFeedBacked() {
  const featureFlagStore = useFeatureFlagStore();
  return computed(() =>
    featureFlagStore.hasFeatureFlag(
      FEATURE_FLAG.TEMP_HUBSPOT_COPILOT_FROM_FEED,
    ),
  );
}

export default function useHubspotCopilotStatus(
  isHubspotCopilot: Ref<boolean>,
) {
  const feedsStore = useFeedsStore();

  const hasFeedBackedFlag = useIsHubspotCopilotFeedBacked();
  const isFeedBacked = computed(
    () => isHubspotCopilot.value && hasFeedBackedFlag.value,
  );

  const hubspotFeed = computed(() =>
    isFeedBacked.value
      ? feedsStore.getFeedByDataSourceType(HS3_DATA_SOURCE_TYPE)
      : undefined,
  );

  const salesEdge = useSalesEdgeIntegrationStatus(
    computed(() => isHubspotCopilot.value && !isFeedBacked.value),
  );

  const feedStatus = computed(() => getFeedStatusUi(hubspotFeed.value));

  const feedStatusName = computed(() => feedStatus.value.type ?? '');

  const feedState = computed(() => {
    if (!feedsStore.ready) return 'checking';
    if (feedsStore.error) return 'unknown';
    if (!hubspotFeed.value) return 'missing';
    return feedStatus.value.severity ? 'known' : 'unknown';
  });

  const statusType = computed<IntegrationProfileStatus>(() => {
    if (!isFeedBacked.value) return salesEdge.statusType.value;
    if (feedState.value === 'missing') return 'error';
    if (feedState.value !== 'known') return 'warning';
    return feedStatus.value.severity ?? 'warning';
  });

  const statusText = computed(() => {
    if (!isFeedBacked.value) return salesEdge.statusText.value;
    if (feedState.value === 'checking') return 'Checking…';
    if (feedState.value === 'unknown') return 'Status unknown';
    if (feedState.value === 'missing') return 'Not connected';
    return feedStatus.value.text ?? 'Status unknown';
  });

  const isActive = computed(() =>
    isFeedBacked.value
      ? feedState.value === 'known' && feedStatusName.value === statuses.ACTIVE
      : salesEdge.isActive.value,
  );

  const needsFeedAttention = computed(() => {
    if (!isFeedBacked.value) return false;
    if (feedState.value === 'missing') return true;
    if (feedState.value !== 'known') return false;
    return !HEALTHY_FEED_STATUSES.includes(feedStatusName.value);
  });

  const feedSettingsRoute = computed(() =>
    hubspotFeed.value
      ? {
          name: 'data-sources-settings-new',
          params: {
            id: hubspotFeed.value.id,
            tab: DataSourceSettingsTabType.GENERAL,
          },
        }
      : { name: 'data-sources' },
  );

  const connectedAt = computed(() =>
    isFeedBacked.value ? hubspotFeed.value?.connected_at : undefined,
  );

  const connectedBy = computed(() =>
    isFeedBacked.value
      ? (hubspotFeed.value?.external_user_email ??
        hubspotFeed.value?.connected_by)
      : undefined,
  );

  return {
    isFeedBacked,
    hubspotFeed,
    statusType,
    statusText,
    isActive,
    needsFeedAttention,
    feedSettingsRoute,
    connectedAt,
    connectedBy,
    isSetupIncomplete: computed(
      () => !isFeedBacked.value && salesEdge.isSetupIncomplete.value,
    ),
    lastSyncAt: computed(() =>
      isFeedBacked.value ? null : salesEdge.lastSyncAt.value,
    ),
    triggerReauthorization: salesEdge.triggerReauthorization,
  };
}
